import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import CollapsibleSection from './CollapsibleSection';
import AudioClipDialog from './AudioClipDialog';
import AudioPlayer from './AudioPlayer';
import { loadSoundfontInstrument } from '../utils/soundfontInstruments';
import { getInstrumentOptions } from '../utils/instrumentOptions';
import { parseExerciseXML, fifthsToMajorTonicPc } from '../utils/exerciseParser';
import { KEYS, transposeExercise, generateExerciseMusicXML, chooseClefForNotes, prettyNoteName } from '../utils/vocalScales';
import { hasPermission } from '../utils/permissionUtils';
import { logVocalExerciseStart, logVocalExerciseComplete } from '../utils/activityLogger';
import * as Tone from 'tone';
import '../styles/ear-training.css';

// Built-in sample so students always have an exercise, and as a format
// reference: a "do re mi re do" written in C major with a trailing rest.
const SAMPLE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<score-partwise version="3.1">
  <work><work-title>Do–Re–Mi–Re–Do</work-title></work>
  <part-list><score-part id="P1"><part-name>Voice</part-name></score-part></part-list>
  <part id="P1">
    <measure number="1">
      <attributes>
        <divisions>1</divisions>
        <key><fifths>0</fifths></key>
        <time><beats>4</beats><beat-type>4</beat-type></time>
        <clef><sign>G</sign><line>2</line></clef>
      </attributes>
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric><syllabic>single</syllabic><text>Do</text></lyric></note>
      <note><pitch><step>D</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric><syllabic>single</syllabic><text>Re</text></lyric></note>
      <note><pitch><step>E</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric><syllabic>single</syllabic><text>Mi</text></lyric></note>
      <note><pitch><step>D</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric><syllabic>single</syllabic><text>Re</text></lyric></note>
    </measure>
    <measure number="2">
      <note><pitch><step>C</step><octave>4</octave></pitch><duration>1</duration><type>quarter</type><lyric><syllabic>single</syllabic><text>Do</text></lyric></note>
      <note><rest/><duration>2</duration><type>half</type></note>
      <note><rest/><duration>1</duration><type>quarter</type></note>
    </measure>
  </part>
</score-partwise>`;

const ExerciseSequencer = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const canManage = hasPermission(user, 'manage-vocal-exercises');

  const [config, setConfig] = useState({
    exerciseId: 'sample',
    startKeyPc: 0, // C
    endKeyPc: 5, // F
    direction: 'updown',
    tempo: 120,
    instrument: 'piano'
  });
  const [library, setLibrary] = useState([]);
  const [libraryError, setLibraryError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { ok, message }
  // Upload/edit dialog: { mode: 'upload'|'edit', exerciseId?, xml?, title, instructions, tempo, audioClips }
  const [editorState, setEditorState] = useState(null);
  const [showClipDialog, setShowClipDialog] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentKeyLabel, setCurrentKeyLabel] = useState(null);
  const [parseError, setParseError] = useState(null);

  const osmdContainerRef = useRef(null);
  const externalOsmdContainer = useRef(null);
  const osmdRef = useRef(null);
  const fileInputRef = useRef(null);
  const playbackTimeouts = useRef([]);
  const sequenceId = useRef(0);
  const instrumentRef = useRef(null);
  const loadedInstrumentKey = useRef(null);

  const instruments = getInstrumentOptions(t);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

  const exercises = useMemo(() => {
    return [
      {
        id: 'sample',
        title: t('vocalExercises.exercises.sampleName'),
        xml: SAMPLE_XML,
        builtIn: true,
        metadata: { instructions: t('vocalExercises.exercises.sampleInstructions'), defaultTempo: 120 }
      },
      ...library
    ];
  }, [library, t]);

  const selectedExercise = exercises.find((e) => String(e.id) === String(config.exerciseId)) || exercises[0];

  const parsed = useMemo(() => {
    if (!selectedExercise) return null;
    try {
      setParseError(null);
      return parseExerciseXML(selectedExercise.xml);
    } catch (err) {
      setParseError(err.message);
      return null;
    }
  }, [selectedExercise]);

  const parseMetadata = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return {};
    }
  };

  // --- Library loading ---
  const loadLibrary = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        operation: 'getContent',
        contentType: 'vocal-exercise',
        status: 'published',
        limit: 50
      });
      const response = await fetch(`${API_BASE}/api/content?${params}`);
      if (!response.ok) throw new Error('Failed to load exercise library');
      const data = await response.json();
      setLibrary(
        (data.content || []).map((item) => ({
          id: item.id,
          title: item.title || `Exercise ${item.id}`,
          xml: item.content_text,
          authorUserId: item.author_user_id,
          metadata: parseMetadata(item.metadata)
        }))
      );
      setLibraryError(null);
    } catch (err) {
      // Local dev has no API; the built-in sample still works
      setLibraryError(err.message);
    }
  }, [API_BASE]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  // --- Upload / edit / delete (library managers only) ---
  // Picking a file validates it and opens the details dialog; the actual
  // save happens in saveEditor
  const handleFileSelected = async (file) => {
    setUploadStatus(null);
    try {
      const xml = await file.text();
      const info = parseExerciseXML(xml); // validate before storing
      setEditorState({
        mode: 'upload',
        xml,
        title: info.title || file.name.replace(/\.(musicxml|xml)$/i, ''),
        instructions: '',
        tempo: info.suggestedTempo && info.suggestedTempo >= 40 && info.suggestedTempo <= 180 ? info.suggestedTempo : 120,
        audioClips: []
      });
    } catch (err) {
      setUploadStatus({ ok: false, message: err.message });
    }
  };

  const saveEditor = async () => {
    if (!editorState) return;
    const metadata = {
      instructions: editorState.instructions.trim() || null,
      defaultTempo: Number(editorState.tempo) || 120,
      audioClips: editorState.audioClips
    };
    try {
      if (editorState.mode === 'upload') {
        const response = await fetch(`${API_BASE}/api/content?operation=createContent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contentType: 'vocal-exercise',
            title: editorState.title.trim() || 'Exercise',
            contentText: editorState.xml,
            status: 'published',
            visibility: 'public',
            category: 'exercise',
            metadata,
            authorUserId: user.id,
            authorName: user.email,
            authorEmail: user.email,
            userRole: user.role,
            userId: user.id
          })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Upload failed');
        }
        const created = await response.json();
        setUploadStatus({ ok: true, message: `${t('vocalExercises.exercises.uploaded')}: ${editorState.title}` });
        await loadLibrary();
        setConfig((prev) => ({ ...prev, exerciseId: created.id }));
      } else {
        const response = await fetch(`${API_BASE}/api/content?operation=updateContent&contentId=${editorState.exerciseId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: editorState.title.trim() || 'Exercise',
            metadata,
            userRole: user.role,
            userId: user.id
          })
        });
        if (!response.ok) throw new Error('Update failed');
        await loadLibrary();
      }
      setEditorState(null);
    } catch (err) {
      setUploadStatus({ ok: false, message: err.message });
      setEditorState(null);
    }
  };

  const handleDelete = async (exercise) => {
    if (!window.confirm(`${t('vocalExercises.exercises.deleteConfirm')} "${exercise.title}"?`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/content?operation=deleteContent&contentId=${exercise.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userRole: user.role, userId: user.id })
      });
      if (!response.ok) throw new Error('Delete failed');
      if (String(config.exerciseId) === String(exercise.id)) {
        setConfig((prev) => ({ ...prev, exerciseId: 'sample' }));
      }
      await loadLibrary();
    } catch (err) {
      setUploadStatus({ ok: false, message: err.message });
    }
  };

  // --- Sequence math ---
  const getSequenceOffsets = useCallback(() => {
    if (!parsed) return { offsets: [], authoredPc: 0, baseOffset: 0 };
    const authoredPc = fifthsToMajorTonicPc(parsed.fifths);
    // Nearest signed transposition from the authored key to the start key
    const baseOffset = ((config.startKeyPc - authoredPc + 6 + 12) % 12) - 6;
    const up = (config.endKeyPc - config.startKeyPc + 12) % 12;
    const down = (config.startKeyPc - config.endKeyPc + 12) % 12;
    let steps;
    if (config.direction === 'up') {
      steps = Array.from({ length: up + 1 }, (_, i) => i);
    } else if (config.direction === 'down') {
      steps = Array.from({ length: down + 1 }, (_, i) => -i);
    } else {
      const ascent = Array.from({ length: up + 1 }, (_, i) => i);
      steps = [...ascent, ...ascent.slice(0, -1).reverse()];
    }
    return { offsets: steps.map((s) => baseOffset + s), authoredPc, baseOffset };
  }, [parsed, config.startKeyPc, config.endKeyPc, config.direction]);

  // --- Notation rendering ---
  const renderExercise = useCallback(
    async (offset) => {
      if (!externalOsmdContainer.current || !parsed) return;
      try {
        if (osmdRef.current) {
          try {
            osmdRef.current.clear();
          } catch (e) {
            // Ignore stale-instance errors
          }
          osmdRef.current = null;
        }
        osmdRef.current = new OpenSheetMusicDisplay(externalOsmdContainer.current, {
          autoResize: true,
          backend: 'svg',
          drawTitle: false,
          drawPartNames: false,
          drawingParameters: 'compacttight'
        });
        const authoredPc = fifthsToMajorTonicPc(parsed.fifths);
        const targetPc = ((authoredPc + offset) % 12 + 12) % 12;
        const { measures, fifths } = transposeExercise(parsed.measures, offset, targetPc);
        const clef = chooseClefForNotes(measures.flat());
        await osmdRef.current.load(generateExerciseMusicXML(measures, fifths, parsed.beats, parsed.beatType, clef));

        const isMobile = window.innerWidth <= 768;
        let zoom = isMobile ? 0.5 : 1.0;
        const minZoom = isMobile ? 0.4 : 0.6;
        osmdRef.current.zoom = zoom;
        osmdRef.current.render();
        const countSystems = () => externalOsmdContainer.current?.querySelectorAll('g.staffline').length || 0;
        while (countSystems() > 2 && zoom > minZoom + 0.001) {
          zoom = Math.max(minZoom, zoom - 0.1);
          osmdRef.current.zoom = zoom;
          osmdRef.current.render();
        }
      } catch (error) {
        console.error('Error rendering exercise notation:', error);
      }
    },
    [parsed]
  );

  // External OSMD container (outside React's reconciler)
  useEffect(() => {
    if (osmdContainerRef.current && !externalOsmdContainer.current) {
      externalOsmdContainer.current = document.createElement('div');
      externalOsmdContainer.current.style.width = '100%';
      osmdContainerRef.current.appendChild(externalOsmdContainer.current);
    }
    return () => {
      if (externalOsmdContainer.current && osmdContainerRef.current) {
        try {
          osmdContainerRef.current.removeChild(externalOsmdContainer.current);
        } catch (e) {
          // Ignore errors during cleanup
        }
        externalOsmdContainer.current = null;
      }
    };
  }, []);

  const stopPlayback = useCallback(() => {
    sequenceId.current += 1;
    playbackTimeouts.current.forEach(clearTimeout);
    playbackTimeouts.current = [];
    if (instrumentRef.current?.releaseAll) instrumentRef.current.releaseAll();
    const svg = externalOsmdContainer.current?.querySelector('svg');
    if (svg) {
      svg.querySelectorAll('g.vf-note *').forEach((el) => {
        el.style.removeProperty('fill');
        el.style.removeProperty('stroke');
      });
    }
    setIsPlaying(false);
    setCurrentKeyLabel(null);
  }, []);

  // Render at the start key whenever the exercise or keys change (idle)
  useEffect(() => {
    stopPlayback();
    const timer = setTimeout(() => {
      const { baseOffset } = getSequenceOffsets();
      renderExercise(baseOffset);
    }, 100);
    return () => clearTimeout(timer);
  }, [getSequenceOffsets, renderExercise, stopPlayback]);

  // Stop timers on unmount
  useEffect(() => {
    return () => {
      playbackTimeouts.current.forEach(clearTimeout);
    };
  }, []);

  // Apply the exercise's default tempo whenever the selection changes
  useEffect(() => {
    const defaultTempo = selectedExercise?.metadata?.defaultTempo;
    if (defaultTempo && defaultTempo >= 40 && defaultTempo <= 180) {
      setConfig((prev) => ({ ...prev, tempo: defaultTempo }));
    }
  }, [selectedExercise]);

  const ensureInstrument = async () => {
    let key = config.instrument;
    if (key === 'random') {
      const realInstruments = Object.keys(instruments).filter((k) => k !== 'random');
      key = realInstruments[Math.floor(Math.random() * realInstruments.length)];
    } else if (instrumentRef.current && loadedInstrumentKey.current === key) {
      return instrumentRef.current;
    }
    const instrument = await loadSoundfontInstrument(instruments[key].soundfont, 0);
    instrumentRef.current = instrument;
    loadedInstrumentKey.current = key;
    return instrument;
  };

  const highlightNote = (groupIndex, durationMs) => {
    const svg = externalOsmdContainer.current?.querySelector('svg');
    if (!svg) return;
    const group = svg.querySelectorAll('g.vf-note')[groupIndex];
    if (!group) return;
    const elements = group.querySelectorAll('*');
    elements.forEach((el) => {
      el.style.setProperty('fill', '#ff4444', 'important');
      el.style.setProperty('stroke', '#ff4444', 'important');
    });
    setTimeout(() => {
      elements.forEach((el) => {
        el.style.removeProperty('fill');
        el.style.removeProperty('stroke');
      });
    }, durationMs);
  };

  const playChord = (instrument, rootMidi, when, durationSec) => {
    const midiToName = (m) => {
      const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      return names[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);
    };
    [rootMidi - 12, rootMidi, rootMidi + 4, rootMidi + 7].forEach((m) => {
      instrument.triggerAttackRelease(midiToName(m), durationSec, when, 0.8);
    });
  };

  const playSequence = async () => {
    if (isPlaying || !parsed) return;
    try {
      await Tone.start();
      const instrument = await ensureInstrument();
      const { offsets, authoredPc } = getSequenceOffsets();
      if (offsets.length === 0) return;

      const seq = ++sequenceId.current;
      setIsPlaying(true);

      const exerciseDetails = {
        name: selectedExercise?.title,
        exerciseId: selectedExercise?.id,
        startKey: prettyNoteName(KEYS[config.startKeyPc].major),
        endKey: prettyNoteName(KEYS[config.endKeyPc].major),
        direction: config.direction,
        tempo: config.tempo,
        instrument: config.instrument,
        repetitions: offsets.length
      };
      logVocalExerciseStart(user, 'guided', exerciseDetails);

      const beat = 60 / config.tempo;
      const events = parsed.measures.flat();
      const totalQuarters = events.reduce((sum, n) => sum + n.quarters, 0);
      const firstNote = events.find((n) => !n.rest);
      const authoredTonicMidi = firstNote.midi - ((((firstNote.midi % 12) + 12) % 12 - authoredPc + 12) % 12);

      // Metric grid: the exercise's written trailing rests are the transition
      // space. Chords land on the beats right after the last note ends, and
      // each repetition starts exactly on a barline. If the written tail is
      // too short for the two chords, the cycle gains one extra measure.
      const qpm = parsed.quartersPerMeasure || 4;
      let lastNoteEnd = 0;
      let walk = 0;
      events.forEach((event) => {
        if (!event.rest) lastNoteEnd = walk + event.quarters;
        walk += event.quarters;
      });
      const chordBeat = Math.ceil(lastNoteEnd - 1e-6); // next beat boundary
      let cycleQuarters = Math.ceil(totalQuarters / qpm - 1e-6) * qpm;
      if (cycleQuarters - chordBeat < 2) cycleQuarters += qpm;

      const midiToName = (m) => {
        const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        return names[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1);
      };

      const schedule = (fn, delayMs) => {
        playbackTimeouts.current.push(
          setTimeout(() => {
            if (sequenceId.current === seq) fn();
          }, Math.max(0, delayMs))
        );
      };

      // exStartSec is the absolute Tone time of this repetition's downbeat,
      // keeping every repetition on the same unbroken metric grid
      const playRep = async (repIndex, exStartSec) => {
        if (sequenceId.current !== seq) return;
        const offset = offsets[repIndex];
        const targetPc = ((authoredPc + offset) % 12 + 12) % 12;
        setCurrentKeyLabel(`${prettyNoteName(KEYS[targetPc].major)} (${repIndex + 1}/${offsets.length})`);
        await renderExercise(offset);
        if (sequenceId.current !== seq) return;

        const root = authoredTonicMidi + offset;
        let exStart = exStartSec;

        // First repetition: one-measure lead-in with the tonic chord on its
        // downbeat; the exercise enters on the next barline
        if (exStart === undefined) {
          const leadIn = Tone.now() + 0.1;
          playChord(instrument, root, leadIn, beat * 2);
          exStart = leadIn + qpm * beat;
        }

        // The exercise itself, with note highlighting
        let cursor = 0; // quarters into the exercise
        events.forEach((event, groupIndex) => {
          const when = exStart + cursor * beat;
          const durationSec = event.quarters * beat;
          if (!event.rest) {
            instrument.triggerAttackRelease(midiToName(event.midi + offset), durationSec * 0.95, when);
            schedule(() => highlightNote(groupIndex, durationSec * 1000), (when - Tone.now()) * 1000);
          }
          cursor += event.quarters;
        });

        const chordTime = exStart + chordBeat * beat;
        if (repIndex < offsets.length - 1) {
          // Two-chord transition on consecutive beats: current key, next key
          const nextRoot = authoredTonicMidi + offsets[repIndex + 1];
          playChord(instrument, root, chordTime, beat * 0.95);
          playChord(instrument, nextRoot, chordTime + beat, beat * 0.95);
          // Wake slightly before the next downbeat so the staff can re-render
          const nextExStart = exStart + cycleQuarters * beat;
          schedule(() => playRep(repIndex + 1, nextExStart), (nextExStart - Tone.now()) * 1000 - 350);
        } else {
          // Final resolution chord, then stop
          playChord(instrument, root, chordTime, beat * 2);
          schedule(() => {
            setIsPlaying(false);
            setCurrentKeyLabel(null);
            playbackTimeouts.current = [];
            // Only sequences that ran all the way through count as completed
            logVocalExerciseComplete(user, 'guided', exerciseDetails);
          }, (chordTime + beat * 3 - Tone.now()) * 1000);
        }
      };

      playRep(0, undefined);
    } catch (error) {
      setIsPlaying(false);
    }
  };

  // --- UI ---
  // Rendered above the layout so it stays visible on mobile, where the rest
  // of the configuration lives in a collapsed section
  const renderExerciseSelector = () => (
    <div className="config-section exercise-selector-row" style={{ marginBottom: 'var(--spacing-md)' }}>
      <label>{t('vocalExercises.exercises.exercise')}:</label>
      <select
        value={config.exerciseId}
        onChange={(e) => setConfig((prev) => ({ ...prev, exerciseId: e.target.value }))}
      >
        {exercises.map((ex) => (
          <option key={ex.id} value={ex.id}>
            {ex.title}
          </option>
        ))}
      </select>

      {canManage && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button
              className="preset-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              ⬆️ {t('vocalExercises.exercises.upload')}
            </button>
            {selectedExercise && !selectedExercise.builtIn && (
              <>
                <button
                  className="preset-btn"
                  onClick={() =>
                    setEditorState({
                      mode: 'edit',
                      exerciseId: selectedExercise.id,
                      title: selectedExercise.title,
                      instructions: selectedExercise.metadata?.instructions || '',
                      tempo: selectedExercise.metadata?.defaultTempo || 120,
                      audioClips: selectedExercise.metadata?.audioClips || []
                    })
                  }
                >
                  ✏️ {t('vocalExercises.exercises.edit')}
                </button>
                <button
                  className="preset-btn"
                  style={{ borderColor: '#dc2626', color: '#fca5a5' }}
                  onClick={() => handleDelete(selectedExercise)}
                >
                  🗑️ {t('vocalExercises.exercises.delete')}
                </button>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,.musicxml"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileSelected(file);
                e.target.value = '';
              }}
            />
          </div>
        )}
      {uploadStatus && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: uploadStatus.ok ? '#86efac' : '#fca5a5' }}>
          {uploadStatus.message}
        </div>
      )}
      {parseError && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#fca5a5' }}>{parseError}</div>
      )}
    </div>
  );

  const renderConfigArea = () => (
    <div className="interval-config">
      <div className="config-header">
        <h4>{t('vocalExercises.config.title')}</h4>
      </div>

      {/* Start key */}
      <div className="config-section">
        <label>{t('vocalExercises.exercises.startKey')}:</label>
        <select
          value={config.startKeyPc}
          onChange={(e) => setConfig((prev) => ({ ...prev, startKeyPc: parseInt(e.target.value) }))}
        >
          {KEYS.map((key, pc) => (
            <option key={pc} value={pc}>
              {prettyNoteName(key.major)}
            </option>
          ))}
        </select>
      </div>

      {/* End key */}
      <div className="config-section">
        <label>{t('vocalExercises.exercises.endKey')}:</label>
        <select
          value={config.endKeyPc}
          onChange={(e) => setConfig((prev) => ({ ...prev, endKeyPc: parseInt(e.target.value) }))}
        >
          {KEYS.map((key, pc) => (
            <option key={pc} value={pc}>
              {prettyNoteName(key.major)}
            </option>
          ))}
        </select>
      </div>

      {/* Direction */}
      <div className="config-section">
        <label>{t('vocalExercises.config.direction')}:</label>
        <div className="mode-buttons">
          {[
            { value: 'up', label: `↗️ ${t('vocalExercises.directions.up')}` },
            { value: 'down', label: `↘️ ${t('vocalExercises.directions.down')}` },
            { value: 'updown', label: `↕️ ${t('vocalExercises.directions.upDown')}` }
          ].map((direction) => (
            <button
              key={direction.value}
              className={`mode-btn ${config.direction === direction.value ? 'active' : ''}`}
              onClick={() => setConfig((prev) => ({ ...prev, direction: direction.value }))}
            >
              {direction.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tempo */}
      <div className="config-section">
        <label>{t('vocalExercises.config.tempo')}:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="range"
            min="40"
            max="180"
            step="5"
            value={config.tempo}
            onChange={(e) => setConfig((prev) => ({ ...prev, tempo: Number(e.target.value) }))}
            style={{ flex: 1 }}
          />
          <span style={{ minWidth: '60px', fontSize: '12px' }}>{config.tempo} BPM</span>
        </div>
      </div>

      {/* Instrument */}
      <div className="config-section">
        <label>{t('vocalExercises.config.instrument')}:</label>
        <select
          value={config.instrument}
          onChange={(e) => setConfig((prev) => ({ ...prev, instrument: e.target.value }))}
        >
          {Object.entries(instruments).map(([key, instrument]) => (
            <option key={key} value={key}>
              {instrument.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="interval-training-container exercise-sequencer-container">
      {renderExerciseSelector()}

      <div className="desktop-layout">
        <CollapsibleSection
          title={t('vocalExercises.config.title')}
          defaultExpanded={false}
          className="config-section-mobile"
        >
          {renderConfigArea()}
        </CollapsibleSection>

        <div className="config-area-desktop">{renderConfigArea()}</div>

        <div className="play-area-container">
          <div className="play-area">
            {selectedExercise?.metadata?.instructions && (
              <div
                style={{
                  margin: '8px 0 0',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#1e293b',
                  border: '1px solid #475569',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap'
                }}
              >
                📝 {selectedExercise.metadata.instructions}
              </div>
            )}
            {selectedExercise?.metadata?.audioClips?.length > 0 && (
              <div style={{ margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {selectedExercise.metadata.audioClips.map((clip, index) => (
                  <AudioPlayer
                    key={`${clip.id}-${index}`}
                    clipName={clip.name}
                    fileUrl={clip.fileUrl}
                    compact={true}
                  />
                ))}
              </div>
            )}
            <div className="notation-container" style={{ padding: '8px', margin: '8px 0' }}>
              <div className="interval-notation-wrapper">
                <div className="interval-title">
                  <h4>
                    {selectedExercise?.title}
                    {currentKeyLabel && (
                      <span style={{ fontWeight: 'normal', fontSize: '13px', marginLeft: '8px', color: '#60a5fa' }}>
                        🎹 {t('vocalExercises.exercises.currentKey')}: {currentKeyLabel}
                      </span>
                    )}
                  </h4>
                </div>
                <div
                  ref={osmdContainerRef}
                  className="interval-notation"
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    filter: 'none',
                    position: 'relative',
                    minHeight: '80px',
                    padding: '4px'
                  }}
                />
              </div>
            </div>

            <div className="play-controls">
              <button
                className="play-interval-btn"
                onClick={isPlaying ? stopPlayback : playSequence}
                disabled={!parsed}
              >
                {isPlaying ? `⏹️ ${t('vocalExercises.stop')}` : `🔊 ${t('vocalExercises.exercises.play')}`}
              </button>
            </div>

            {libraryError && canManage && (
              <div style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                {t('vocalExercises.exercises.libraryUnavailable')}
              </div>
            )}
          </div>
        </div>
      </div>

      {editorState && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 900
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditorState(null);
          }}
        >
          <div
            style={{
              background: '#1e293b',
              border: '1px solid #475569',
              borderRadius: '10px',
              padding: '20px',
              width: 'min(440px, 92vw)',
              color: '#f1f5f9'
            }}
          >
            <h4 style={{ margin: '0 0 14px' }}>
              {editorState.mode === 'upload'
                ? `⬆️ ${t('vocalExercises.exercises.upload')}`
                : `✏️ ${t('vocalExercises.exercises.edit')}`}
            </h4>

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: '#94a3b8' }}>
              {t('vocalExercises.exercises.titleLabel')}
            </label>
            <input
              type="text"
              value={editorState.title}
              onChange={(e) => setEditorState((prev) => ({ ...prev, title: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                marginBottom: '12px',
                borderRadius: '6px',
                border: '1px solid #475569',
                background: '#0f172a',
                color: '#f1f5f9',
                boxSizing: 'border-box'
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: '#94a3b8' }}>
              {t('vocalExercises.exercises.instructions')}
            </label>
            <textarea
              value={editorState.instructions}
              onChange={(e) => setEditorState((prev) => ({ ...prev, instructions: e.target.value }))}
              rows={4}
              placeholder={t('vocalExercises.exercises.instructionsHint')}
              style={{
                width: '100%',
                padding: '8px 10px',
                marginBottom: '12px',
                borderRadius: '6px',
                border: '1px solid #475569',
                background: '#0f172a',
                color: '#f1f5f9',
                resize: 'vertical',
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            />

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: '#94a3b8' }}>
              {t('vocalExercises.exercises.audioExamples')}
            </label>
            <div style={{ marginBottom: '12px' }}>
              {editorState.audioClips.map((clip, index) => (
                <div
                  key={`${clip.id}-${index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '6px 10px',
                    marginBottom: '4px',
                    borderRadius: '6px',
                    background: '#0f172a',
                    border: '1px solid #475569',
                    fontSize: '13px'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    🎙️ {clip.name}
                  </span>
                  <button
                    onClick={() =>
                      setEditorState((prev) => ({
                        ...prev,
                        audioClips: prev.audioClips.filter((_, i) => i !== index)
                      }))
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '0 2px'
                    }}
                    title={t('vocalExercises.exercises.delete')}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="preset-btn" onClick={() => setShowClipDialog(true)}>
                🎙️ {t('vocalExercises.exercises.addExample')}
              </button>
            </div>

            <label style={{ display: 'block', fontSize: '13px', marginBottom: '4px', color: '#94a3b8' }}>
              {t('vocalExercises.exercises.defaultTempo')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
              <input
                type="range"
                min="40"
                max="180"
                step="5"
                value={editorState.tempo}
                onChange={(e) => setEditorState((prev) => ({ ...prev, tempo: Number(e.target.value) }))}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '60px', fontSize: '12px' }}>{editorState.tempo} BPM</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                className="preset-btn"
                onClick={() => setEditorState(null)}
              >
                {t('vocalExercises.exercises.cancel')}
              </button>
              <button
                className="preset-btn"
                style={{ borderColor: '#3b82f6', color: '#93c5fd' }}
                onClick={saveEditor}
              >
                💾 {t('vocalExercises.exercises.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <AudioClipDialog
        isOpen={showClipDialog}
        onClose={() => setShowClipDialog(false)}
        initialTab="record"
        onClipSelected={(clip) => {
          setEditorState((prev) =>
            prev
              ? {
                  ...prev,
                  audioClips: [...prev.audioClips, { id: clip.id, name: clip.name, fileUrl: clip.file_url }]
                }
              : prev
          );
          setShowClipDialog(false);
        }}
      />
    </div>
  );
};

export default ExerciseSequencer;
